import { useCallback, useSyncExternalStore } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import {
	extractConnectorInfo,
	filterCommonAttributes,
} from '../../../../ai-answer-dialog/analytics-helpers';
import type {
	commonAttributesType,
	NLPSearchResultSource,
} from '../../../../ai-answer-dialog/types';
import { getSnapshot, markFired, subscribe } from '../sain-usage-event-store';

import { SainAction } from './sain-action';

export const SAIN_USAGE_DWELL_THRESHOLD = 3000;

export type SAINUsageEventPayload = {
	metCriteria?: SainAction;
	dwellTime?: number;
	commonAttributes?: commonAttributesType;
	sourceProduct?: string;
	/**
	 * The single source associated with the event, if applicable. When provided for a
	 * `SOURCE_CLICKED` action, the `connectorInfo` attribute is overridden so it only reflects
	 * the clicked source rather than every source in the answer (gated by
	 * `kd_sain_connector_info_analytics`).
	 */
	source?: NLPSearchResultSource | null;
};

export const useSAINUsageCriteriaMet: () => {
	fireSAINUsageEvent: ({
		metCriteria,
		dwellTime,
		commonAttributes,
		sourceProduct,
		source,
	}: SAINUsageEventPayload) => void;
} = () => {
	const { trackAIResultView, trackAIResultAction, trackAIFeedbackSubmit } =
		useAIEventsInstrumentation();
	// eslint-disable-next-line @atlaskit/platform/ensure-use-sync-external-store-server-snapshot
	useSyncExternalStore(subscribe, getSnapshot);

	const fireSAINUsageEvent = useCallback(
		({
			metCriteria,
			dwellTime,
			commonAttributes,
			sourceProduct,
			source,
		}: SAINUsageEventPayload): void => {
			const sanitizedCommonAttributes = commonAttributes
				? filterCommonAttributes(commonAttributes)
				: {};
			// Handle FMP dwell cases - fire initial event with doesNotMeetMAUCriteria = true
			if (
				metCriteria === SainAction.FMP_DWELL_START ||
				metCriteria === SainAction.FMP_DWELL_START_EXPANDED
			) {
				trackAIResultView({
					doesNotMeetMAUCriteria: true,
					attributes: {
						mauEligibilityCriteria: metCriteria,
						met_fmp_criteria: false,
						sourceProduct,
						...sanitizedCommonAttributes,
					},
				});
				return;
			}

			if (metCriteria === SainAction.FMP_DWELL_REACHED) {
				// If dwell time reaches threshold, fire second event (could eventually count as usage criteria)
				trackAIResultView({
					doesNotMeetMAUCriteria: true, // Keep as true for now, could change based on sizing exercise
					attributes: {
						dwellTime,
						mauEligibilityCriteria: metCriteria,
						met_fmp_criteria: true,
						sourceProduct,
						...sanitizedCommonAttributes,
					},
				});
				return;
			}

			if (metCriteria === SainAction.DEFINITION_DWELL_MARKED) {
				// If dwell time reaches threshold, fire second event (could eventually count as usage criteria)
				trackAIResultView({
					doesNotMeetMAUCriteria: true, // DS team (Ala Qabaja & Nan Ma) wants to keep this true
					attributes: {
						dwellTime,
						sourceProduct,
						aiDwellTimeMilliSeconds: dwellTime, // new attribute requested by DS team
						...sanitizedCommonAttributes,
					},
				});
				return;
			}

			if (metCriteria === SainAction.FMP_RESULT_EXITED) {
				trackAIResultView({
					doesNotMeetMAUCriteria: true, // Keep as true. This viewed event is for QSR sizing & is not used for MAU.
					attributes: {
						action: metCriteria,
						dwellTime,
						sourceProduct,
						trackingMethod: 'fmp',
						...sanitizedCommonAttributes,
					},
				});
				return;
			}

			if (metCriteria) {
				// If usage criteria are provided, emit a viewed event with doesNotMeetMAUCriteria set to false
				// only once per SAIN request
				const requestId = commonAttributes?.extraAttributes?.requestId;
				const doesNotMeetMAUCriteria = fg('kd_fix_sain_mau_instrumentation')
					? requestId
						? getSnapshot().has(requestId)
						: true // new behavior: need a requestId to meet MAU criteria to ensure only one MAU-met event is fired
					: false; // old behavior: always met MAU criteria

				trackAIResultView({
					doesNotMeetMAUCriteria,
					attributes: {
						dwellTime: dwellTime || 0,
						mauEligibilityCriteria: metCriteria,
						sourceProduct,
						...sanitizedCommonAttributes,
						// When a specific SAIN source is clicked, surface the clicked source's product
						// as its own attribute on the `aiResult viewed` event (gated behind
						// `kd_sain_source_product_clicked`).
						...(metCriteria === SainAction.SOURCE_CLICKED &&
							source?.product != null &&
							fg('kd_sain_source_product_clicked') && {
								sourceProductClicked: source.product,
							}),
					},
				});
				if (!doesNotMeetMAUCriteria) {
					markFired(requestId);
				}
				// In addition, emit an action/feedback event for the action that was met
				// (https://hello.atlassian.net/wiki/x/Vxb5DAE)
				switch (metCriteria) {
					case SainAction.SOURCE_CLICKED: {
						// Override the `connectorInfo` attribute so it only reflects the single clicked
						// source rather than every source in the answer. `extractConnectorInfo` handles
						// the `kd_sain_connector_info_analytics` gate internally (returning `undefined`
						// when disabled), so when the gate is off we fall back to the existing behavior
						// of not including `connectorInfo` for this action.
						const clickedConnectorInfo = source ? extractConnectorInfo([source], false) : undefined;
						trackAIResultAction(metCriteria, {
							attributes: {
								dwellTime,
								sourceProduct,
								...sanitizedCommonAttributes,
								...(clickedConnectorInfo !== undefined && {
									connectorInfo: clickedConnectorInfo,
								}),
							},
						});
						break;
					}
					case SainAction.COPY_CLICKED:
					case SainAction.FOLLOWUP_CLICKED:
					case SainAction.TOPIC_CLICKED:
					case SainAction.RECENT_WORK_CLICKED:
					case SainAction.COLLABORATOR_CLICKED:
					case SainAction.VIEW_PROFILE_CLICKED:
					case SainAction.TEAM_MEMBER_CLICKED:
					case SainAction.VIEW_TEAM_PAGE_CLICKED:
					case SainAction.OPEN_CHAT_CLICKED:
					case SainAction.JIRA_NL_TO_JQL_VIEW_ALL:
					case SainAction.JIRA_NL_TO_JQL_WORK_ITEM_CLICKED:
					case SainAction.VIEW_ALL_SOURCES:
					case SainAction.BLUE_LINK_CLICKED:
						trackAIResultAction(metCriteria, {
							attributes: { dwellTime, sourceProduct, ...sanitizedCommonAttributes },
						});
						break;
					case SainAction.POSITIVE_FEEDBACK_PROVIDED:
						trackAIFeedbackSubmit('up', {
							attributes: { dwellTime, ...sanitizedCommonAttributes },
						});
						break;
					case SainAction.NEGATIVE_FEEDBACK_PROVIDED:
						trackAIFeedbackSubmit('down', {
							attributes: { dwellTime, ...sanitizedCommonAttributes },
						});
						break;
					case SainAction.AI_MODE_FOLLOWUP_CLICKED:
						trackAIResultAction(metCriteria, {
							attributes: { dwellTime, sourceProduct, ...sanitizedCommonAttributes },
						});
						break;
					default:
						break;
				}
			} else {
				// If no usage criteria are provided, emit a viewed event with doesNotMeetMAUCriteria set to true
				trackAIResultView({
					doesNotMeetMAUCriteria: true,
					attributes: {
						dwellTime: dwellTime || 0,
						...sanitizedCommonAttributes,
					},
				});
			}
		},

		[trackAIResultView, trackAIResultAction, trackAIFeedbackSubmit],
	);

	return { fireSAINUsageEvent };
};
